import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { AccessCentrePermission } from 'domains/accessCentre/types';
import PermissionCard from '../PermissionCard';

/** Human-readable module name (e.g. "access_management" → "Access management"). */
const formatModuleName = (moduleKey: string): string => {
    if (!moduleKey) return moduleKey;
    const withSpaces = moduleKey.replace(/_/g, ' ');
    return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1).toLowerCase();
};

const permissionsGridStyles = {
    display: 'grid',
    gridTemplateColumns: {
        xs: '1fr',
        sm: 'repeat(2, 1fr)',
        md: 'repeat(3, 1fr)',
    },
    gap: 1.5,
};

export type PermissionModuleCardProps = {
    moduleKey: string;
    permissions: AccessCentrePermission[];
    selectedPermissionIds: number[];
    onTogglePermission: (id: number) => void;
    readOnly?: boolean;
};

const PermissionModuleCard = ({
    moduleKey,
    permissions,
    selectedPermissionIds,
    onTogglePermission,
    readOnly = false,
}: PermissionModuleCardProps) => (
    <Accordion
        disableGutters
        elevation={0}
        sx={{
            '&:before': { display: 'none' },
            borderBottom: '1px solid',
            borderColor: 'divider',
        }}
    >
        <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${moduleKey}-permissions`}
            id={`${moduleKey}-header`}
            data-testid={`access-center-permissions-module-${moduleKey}`}
        >
            <Typography variant="subtitle1" fontWeight={500}>
                {formatModuleName(moduleKey)}
            </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
            <Box
                sx={{
                    ...permissionsGridStyles,
                    '& > *': { minWidth: 0 },
                }}
            >
                {permissions.map((permission) => (
                    <PermissionCard
                        key={permission.id}
                        permission={permission}
                        label={permission?.name}
                        isSelected={selectedPermissionIds.includes(permission.id)}
                        onToggle={onTogglePermission}
                        readOnly={readOnly}
                    />
                ))}
            </Box>
        </AccordionDetails>
    </Accordion>
);

export default PermissionModuleCard;
